/**
 * KD-Tree Spatial Index for 3D Point Cloud
 * 
 * Enables fast spatial queries:
 * - Nearest neighbor search: Find closest point/camera to a position
 * - Radius search: Find all points/cameras within distance
 * - K-nearest search: Find K closest points/cameras
 * 
 * Performance: O(log n) for balanced tree
 * Typical query time: <5ms for 10,000 points
 */

import { Camera, Point3D } from '@/types/colmap'

/**
 * Generic 3D point interface for KD-Tree
 */
export interface Point3DPosition {
  id: number
  position: [number, number, number]
}

/**
 * KD-Tree node structure
 */
interface KDNode {
  point: Point3DPosition
  left: KDNode | null
  right: KDNode | null
  axis: number // 0 = x, 1 = y, 2 = z
}

/**
 * Result from nearest neighbor search
 */
export interface NearestResult {
  point: Point3DPosition
  distance: number
}

/**
 * KD-Tree for efficient 3D spatial queries
 */
export class KDTree {
  private root: KDNode | null = null
  private pointsArray: Point3DPosition[] = []
  
  /**
   * Build KD-Tree from array of 3D points
   * 
   * @param points - Array of points with id and position
   */
  constructor(points: Point3DPosition[]) {
    const startTime = performance.now()
    this.pointsArray = [...points]
    this.root = this.buildTree(points, 0)
    const elapsedMs = performance.now() - startTime
    console.log(`[KDTree] Built: ${points.length} points (${elapsedMs.toFixed(0)}ms)`)
  }
  
  /**
   * Recursively build KD-Tree
   * 
   * @param points - Points to build tree from
   * @param depth - Current depth (determines split axis)
   * @returns Root node of subtree
   */
  private buildTree(points: Point3DPosition[], depth: number): KDNode | null {
    if (points.length === 0) return null
    
    // Choose axis based on depth (x, y, z rotation)
    const axis = depth % 3
    
    // Sort points by current axis
    points.sort((a, b) => a.position[axis] - b.position[axis])
    
    // Choose median as pivot
    const medianIndex = Math.floor(points.length / 2)
    const medianPoint = points[medianIndex]
    
    // Create node and recursively build subtrees
    return {
      point: medianPoint,
      axis,
      left: this.buildTree(points.slice(0, medianIndex), depth + 1),
      right: this.buildTree(points.slice(medianIndex + 1), depth + 1)
    }
  }
  
  /**
   * Find K nearest neighbors to a query position
   * 
   * @param position - Query position [x, y, z]
   * @param k - Number of neighbors to find
   * @returns Array of nearest neighbors sorted by distance (closest first)
   */
  public kNearest(position: [number, number, number], k: number): NearestResult[] {
    const results: NearestResult[] = []
    this.kNearestRecursive(this.root, position, k, results)
    results.sort((a, b) => a.distance - b.distance)
    return results.slice(0, k)
  }
  
  /**
   * Recursive K-nearest search
   */
  private kNearestRecursive(
    node: KDNode | null,
    target: [number, number, number],
    k: number,
    results: NearestResult[]
  ): void {
    if (!node) return
    
    const distance = this.distance(node.point.position, target)
    
    // Add current node to results
    if (results.length < k) {
      results.push({ point: node.point, distance })
      results.sort((a, b) => b.distance - a.distance) // Max heap (furthest first)
    } else if (distance < results[0].distance) {
      // Replace furthest point
      results[0] = { point: node.point, distance }
      results.sort((a, b) => b.distance - a.distance)
    }
    
    // Determine which side to search first
    const axis = node.axis
    const diff = target[axis] - node.point.position[axis]
    const nearSide = diff < 0 ? node.left : node.right
    const farSide = diff < 0 ? node.right : node.left
    
    // Search near side first
    this.kNearestRecursive(nearSide, target, k, results)
    
    // Check if we need to search far side
    // Only search if: (1) we haven't found k points yet, OR
    //                 (2) the far side could contain closer points
    if (results.length < k || Math.abs(diff) < results[0].distance) {
      this.kNearestRecursive(farSide, target, k, results)
    }
  }
  
  /**
   * Find nearest single neighbor to a query position
   * 
   * @param position - Query position [x, y, z]
   * @returns Nearest neighbor, or null if tree is empty
   */
  public nearest(position: [number, number, number]): NearestResult | null {
    const results = this.kNearest(position, 1)
    return results.length > 0 ? results[0] : null
  }
  
  /**
   * Find all points within a radius of a query position
   * 
   * @param position - Query position [x, y, z]
   * @param radius - Search radius in world units
   * @returns Array of points within radius, sorted by distance
   */
  public withinRadius(position: [number, number, number], radius: number): NearestResult[] {
    const startTime = performance.now()
    const results: NearestResult[] = []
    
    this.withinRadiusRecursive(this.root, position, radius, results)
    
    // Sort by distance (closest first)
    results.sort((a, b) => a.distance - b.distance)
    
    const elapsedMs = performance.now() - startTime
    if (elapsedMs > 5) {
      console.warn(`⚠️ KD-Tree withinRadius query slow: ${elapsedMs.toFixed(2)}ms for ${results.length} results`)
    }
    
    return results
  }
  
  /**
   * Recursive radius search
   */
  private withinRadiusRecursive(
    node: KDNode | null,
    target: [number, number, number],
    radius: number,
    results: NearestResult[]
  ): void {
    if (!node) return
    
    const distance = this.distance(node.point.position, target)
    
    // Add point if within radius
    if (distance <= radius) {
      results.push({ point: node.point, distance })
    }
    
    // Check which subtrees to search
    const axis = node.axis
    const diff = target[axis] - node.point.position[axis]
    
    // Always search near side
    if (diff < 0) {
      this.withinRadiusRecursive(node.left, target, radius, results)
      // Search far side if it could contain points within radius
      if (Math.abs(diff) <= radius) {
        this.withinRadiusRecursive(node.right, target, radius, results)
      }
    } else {
      this.withinRadiusRecursive(node.right, target, radius, results)
      // Search far side if it could contain points within radius
      if (Math.abs(diff) <= radius) {
        this.withinRadiusRecursive(node.left, target, radius, results)
      }
    }
  }
  
  /**
   * Calculate Euclidean distance between two 3D points
   */
  private distance(p1: [number, number, number], p2: [number, number, number]): number {
    const dx = p1[0] - p2[0]
    const dy = p1[1] - p2[1]
    const dz = p1[2] - p2[2]
    return Math.sqrt(dx * dx + dy * dy + dz * dz)
  }
  
  /**
   * Get maximum depth of tree (for debugging)
   */
  private getDepth(node: KDNode | null): number {
    if (!node) return 0
    return 1 + Math.max(this.getDepth(node.left), this.getDepth(node.right))
  }
  
  /**
   * Get total number of points in tree
   */
  public size(): number {
    return this.pointsArray.length
  }
}

/**
 * Create KD-Tree from COLMAP Point3D array
 */
export function createPointKDTree(points: Point3D[]): KDTree {
  const kdPoints: Point3DPosition[] = points.map(p => ({
    id: p.id,
    position: p.position
  }))
  return new KDTree(kdPoints)
}

/**
 * Create KD-Tree from COLMAP Camera array (using camera positions)
 */
export function createCameraKDTree(cameras: Camera[]): KDTree {
  const kdPoints: Point3DPosition[] = cameras.map(c => ({
    id: c.id,
    position: c.position
  }))
  return new KDTree(kdPoints)
}

/**
 * Find cameras within a spatial cone (for swipe direction navigation)
 * 
 * @param cameraTree - KD-Tree of camera positions
 * @param origin - Origin position (current camera position)
 * @param direction - Normalized direction vector [x, y, z]
 * @param maxDistance - Maximum distance to search (e.g., 2 meters)
 * @param minDotProduct - Minimum dot product for cone (0.7 = 45° cone)
 * @param cameras - Array of all cameras (to get full camera objects)
 * @returns Cameras within cone, sorted by relevance (dot product * distance factor)
 */
export function findCamerasInCone(
  cameraTree: KDTree,
  origin: [number, number, number],
  direction: [number, number, number],
  maxDistance: number,
  minDotProduct: number,
  cameras: Camera[]
): Camera[] {
  // First, get all cameras within radius
  const nearby = cameraTree.withinRadius(origin, maxDistance)
  
  // Filter by cone angle and sort by relevance
  const cameraMap = new Map(cameras.map(c => [c.id, c]))
  
  const results = nearby
    .map(result => {
      const camera = cameraMap.get(result.point.id)
      if (!camera) return null
      
      // Calculate direction from origin to camera
      const dx = camera.position[0] - origin[0]
      const dy = camera.position[1] - origin[1]
      const dz = camera.position[2] - origin[2]
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      
      if (dist < 0.01) return null // Skip self (distance ~0)
      
      // Normalize direction to camera
      const dirX = dx / dist
      const dirY = dy / dist
      const dirZ = dz / dist
      
      // Calculate dot product with desired direction
      const dotProduct = dirX * direction[0] + dirY * direction[1] + dirZ * direction[2]
      
      // Filter by cone angle
      if (dotProduct < minDotProduct) return null
      
      // Score: prefer cameras closer to direction, but also consider distance
      const score = dotProduct / (1 + dist * 0.5) // Favor closer cameras
      
      return { camera, dotProduct, distance: dist, score }
    })
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .sort((a, b) => b.score - a.score) // Best score first
    .map(r => r.camera)
  
  return results
}
